import { animate, style, transition, trigger } from "@angular/animations";
import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  HostBinding,
  Input,
  OnDestroy,
  OnInit,
  Output,
  signal,
  viewChild
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ButtonModule, ColorModeService } from '@coreui/angular-pro';
import { IconDirective } from '@coreui/icons-angular';
import { ServerAuthUrlPipe } from 'auth';
import { ConferenceJitsiComponent, ConferenceZoomComponent } from 'conference';
import { I18nService, TranslatePipe } from 'i18n';
import { NgxExtendedPdfViewerModule } from 'ngx-extended-pdf-viewer';
import { combineLatest, distinctUntilChanged, startWith } from 'rxjs';
import { DownloadService, Logger, SafeUrlPipe } from 'shared';
import { MediaService } from '../../media.service';
import { Media } from '../../models/media';
import { VideoPlayerComponent } from '../video-player/video-player.component';

declare type AnimationTiming = { enterTime: string, leaveTime: string };
declare type AnimationType = 'fadeInOut' | 'slideInOut';

@Component({
  selector: 'media-viewer',
  imports: [
    CommonModule,
    ButtonModule,
    NgxExtendedPdfViewerModule,
    SafeUrlPipe,
    VideoPlayerComponent,
    ConferenceJitsiComponent,
    ConferenceZoomComponent,
    IconDirective,
    ServerAuthUrlPipe,
    TranslatePipe
  ],
  templateUrl: './media-viewer.component.html',
  styleUrls: ['./media-viewer.component.scss'],
  standalone: true,
  animations: [
      // trigger('slideInOut', [
      //     transition(':enter', [
      //         style({ maxHeight: 0, opacity: 0 }),
      //         animate('{{ enterTime }}', style({ maxHeight: '3em', opacity: 1 }))
      //     ]),
      //     transition(':leave', [
      //         style({ maxHeight: '3em', opacity: 1 }),
      //         animate('{{ leaveTime }}', style({ maxHeight: 0, opacity: 0 }))
      //     ])
      // ]),
      trigger('fadeInOut', [
          transition(':enter', [
              style({ opacity: 0 }),
              animate('{{ enterTime }}', style({ opacity: 1 }))
          ]),
          transition(':leave', [
              style({ opacity: 1 }),
              animate('{{ leaveTime }}', style({ opacity: 0 }))
          ])
      ])
  ],
  host: {
    '[class.toolbar-visible]': 'toolbarDisplayed()',
  },
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class MediaViewerComponent implements OnInit, OnDestroy, AfterViewInit {

  videoPlayer = viewChild<VideoPlayerComponent>('videoPlayer');

  @HostBinding ('class.conference') conference = false;
  @Input() hasToolbar = true;

  _media: Media | undefined = undefined;
  @Input() set media(media: Media) {
    this._media = media;
    const link = media.links?.[0];
    const src = link?.src;
    if (src?.endsWith('/index.html')) {
      // in production builds service worker interferes with paths ending in index.html
      const bypassSrc = src.includes('?') 
      ? `${src}&ngsw-bypass`
      : `${src}?ngsw-bypass`;
        media.links![0] = {...link, src: bypassSrc};
    } else {
      this._media = media;
    }
  }
  get media(): Media | undefined {
    return this._media;
  }

  @Output() onAction = new EventEmitter<string>();

  Media = Media;
  theme: 'light' | 'dark' = this.colorModeService.colorMode() == 'dark' ? 'dark': 'light';

  toolbarDisplayed = signal(this.hasToolbar);

  protected screenOrientationType: string | null = null;

  protected static animationTimingDefinitions: { [animationType in AnimationType]: AnimationTiming }
  protected static initialized = false;
  static initialize () {
    if (!this.initialized) {
      this.animationTimingDefinitions = {
        slideInOut: {
          enterTime: '300ms',
          leaveTime: '300ms'
        },
        fadeInOut: {
          enterTime: '0.5s',
          leaveTime: '1s'
        }
      }
      this.initialized = true;
    }
  }

  animationTimings = MediaViewerComponent.animationTimingDefinitions;

  protected logger = new Logger('MediaViewerComponent')

  constructor(public mediaService: MediaService, 
              public i18nService: I18nService,
              public colorModeService: ColorModeService,
              protected destroyRef: DestroyRef,
              protected downloadService: DownloadService,
              protected elementRef: ElementRef<HTMLElement>) {
  }

  /**
   * True for an image or video whose media declares `download` (boolean or the
   * JSON string "true"). PDFs are left out: the PDF viewer has a download
   * button in its own toolbar.
   */
  downloadable(): boolean {
    const media = this.media;
    if ((media?.type !== 'image' && media?.type !== 'video') || !media.links?.[0]?.src) {
      return false;
    }
    const flag: unknown = media.properties?.['download'];
    return typeof flag === 'string' ? flag.toLowerCase() === 'true' : flag === true;
  }

  download(): void {
    const media = this.media;
    const src = media?.links?.[0]?.src;
    if (!media || !src) {
      return;
    }
    // Explorer files are named with their extension; a JSON item's name is a
    // title, so DownloadService derives the file name from the URL for those.
    const filename = /\.[a-z0-9]{2,5}$/i.test(media.name) ? media.name : undefined;
    this.downloadService
        .download(src, this.elementRef.nativeElement, { filename, errorMessage: 'download.failed' })
        .catch(error => this.logger.warn('Failed to download media', { media, error }));
  }

  ngOnInit() {
  }

  ngAfterViewInit() {
    if (!this.media?.properties?.played) {
      this.onAction.emit('played');
    }
    const videoPlayer = this.videoPlayer();
    if (videoPlayer) {
      combineLatest([
          videoPlayer.userActive,
          videoPlayer.playing,
          videoPlayer.fullscreen
                     .pipe(
                        startWith(videoPlayer.isFullscreen()),
                        distinctUntilChanged()
                      )
        ])
        .pipe(
          takeUntilDestroyed(this.destroyRef)
        )
        .subscribe(([userActive, playing, fullscreen]: [boolean, boolean, boolean]) => {
          if (this.hasToolbar) {
            // Remove !fullscreen condition to display toolbar when
            // we have custom videojs fullscreen control 
            // which puts the whole videojs container element including the toolbar in fullscreen 
            // i.e. not just the player
            this.toolbarDisplayed.set((userActive || !playing) && !fullscreen);
          }
        });
    }
  }

  ngOnDestroy(): void {
  }
  
  // iframeError = false;
  onIframeError(event: Event) {
    this.logger.error('Iframe error', event);
    // this.iframeError = true;
  }

  onTapAction(action: any) {
    this.logger.debug('onTapAction', {media: this.media, action});
    if (action.id == 'close') {
      this.close();
    } else {
      this.onAction.emit(action.id);
    }
  }

  close() {
    this.onAction.emit('close');
  }
}
MediaViewerComponent.initialize();